import { useCallback, useEffect, useRef, useState } from "react";
import $api from "@/zapi-frontend/shared/api/axios";
import { useChapterMemoStore } from "@/zapi-frontend/stores/chapterMemo";

export type TChapterMemoStatus = {
  id: number;
  hasActiveMemo: boolean;
  chapterName: string;
};

type TChapterMemoStatusResponse = {
  chapters: TChapterMemoStatus[];
};

const MAX_CONSECUTIVE_POLL_ERRORS = 10;

export function useChapterMemoPolling(projectId: string, enabled = true, pollingInterval = 3000) {
  const { setChapters, getChapters, clearChapters } = useChapterMemoStore();
  const chapters = getChapters(projectId);
  const [isPolling, setIsPolling] = useState(false);
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const previousChapterCountRef = useRef<number>(0);
  const consecutiveErrorsRef = useRef(0);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const stopPolling = useCallback(() => {
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
    setIsPolling(false);
  }, []);

  const poll = useCallback(async () => {
    if (!projectId || !enabled) {
      stopPolling();
      return;
    }

    try {
      const response = await $api.get<TChapterMemoStatusResponse>(`/projects/${projectId}/chapters-memo-status`);

      if (!enabledRef.current) return;

      consecutiveErrorsRef.current = 0;
      const newChapters = response.data.chapters;
      setChapters(projectId, newChapters);

      const completedCount = newChapters.filter((ch) => ch.hasActiveMemo).length;

      if (completedCount > previousChapterCountRef.current) {
        // eslint-disable-next-line no-console
        console.log(`[CHAPTER_MEMO_POLLING] ${completedCount}/${newChapters.length} chapters completed`);
      }
      previousChapterCountRef.current = completedCount;

      setIsPolling(true);
      pollingTimeoutRef.current = setTimeout(poll, pollingInterval);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error polling chapter memo status:", error);
      consecutiveErrorsRef.current += 1;
      // A transient failure must not kill the loop for good — the sidebar spinners of
      // generating chapters read isPolling, and a dead loop hides them until reload.
      if (consecutiveErrorsRef.current >= MAX_CONSECUTIVE_POLL_ERRORS) {
        stopPolling();
        return;
      }
      if (!enabledRef.current) return;
      setIsPolling(true);
      pollingTimeoutRef.current = setTimeout(poll, pollingInterval);
    }
  }, [projectId, enabled, pollingInterval, stopPolling, setChapters]);

  useEffect(() => {
    if (enabled && projectId) {
      poll();
    }

    return () => {
      stopPolling();
    };
  }, [projectId, enabled, poll, stopPolling]);

  useEffect(() => {
    if (!enabled && projectId) {
      clearChapters(projectId);
      previousChapterCountRef.current = 0;
    }
  }, [enabled, projectId, clearChapters]);

  return {
    chapters: enabled ? chapters : [],
    isPolling,
    stopPolling
  };
}
